import React, { FC, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FormInstance } from "antd";
import {
  AppButton,
  AuthLayout,
  Form,
  FORM_FIELD_TYPES,
  IFormItems,
} from "site/ui/base";
import { routeNames } from "utils";

const ButtonContainer = styled.div`
  margin-top: 1.5rem;
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #666;

  a {
    color: #5575f7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
interface IResetPasswordData {
  email: string;
  token: string;
  password: string;
  confirmPassword: string;
}
export interface ResetPasswordProps {
  email: string;
  token: string;

  onResetPassword: (data: IResetPasswordData) => Promise<boolean>;
  onMoveToLogin: () => void;
}
export const ResetPassword:FC<ResetPasswordProps> = ({ email, token, onResetPassword, onMoveToLogin }) => {
  const formRef = useRef<FormInstance<IResetPasswordData>>(null);

  const [loading, setLoading] = useState(false)

  const handleSubmit = useCallback(async (values: IResetPasswordData) => {
    setLoading(true)
    const res = await onResetPassword({...values, token });
    setLoading(false)
    if(res){
      onMoveToLogin()
    }
  }, [token, onResetPassword]);
  const onUpdate = useCallback(() => {
    formRef.current?.submit();
  }, [token, onResetPassword]);
  return (
    <AuthLayout
      title="Set up a new password"
      description="Please enter your new password below. Make sure it's secure and easy to remember."
    >
      <Form
        formRef={formRef}
        items={[
          {
            fieldType: FORM_FIELD_TYPES.TEXT,
            itemProps: {
              name: "email",
              label: "Email address",
              rules: [{ required: true, message: "Please input your email!" }],
            },
            fieldProps: {
              placeholder: "esosa@example.art",
              size: "large",
              disabled: true,
            },
          },
          {
            fieldType: FORM_FIELD_TYPES.PASSWORD,
            itemProps: {
              name: "password",
              label: "New Password",
              rules: [
                { required: true, message: "Please input your new password!" },
                { min: 8, message: "Password must be at least 8 characters long" },
              ],
            },
            fieldProps: {
              placeholder: "• • • • • • •",
              size: "large",
            },
          },
          {
            fieldType: FORM_FIELD_TYPES.PASSWORD,
            itemProps: {
              name: "confirmPassword",
              label: "Confirm Password",
              rules: [
                { required: true, message: "Please confirm your password!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("The two passwords do not match!")
                    );
                  },
                }),
              ],
            },
            fieldProps: {
              placeholder: "• • • • • • •",
              size: "large",
            },
          },
        ]}
        formProps={{
          onFinish: handleSubmit,
          layout: "vertical",
          initialValues: {
            email,
            token,
          }
        }}
      />

      <ButtonContainer>
        <AppButton loading={loading} variant="primary" fullWidth onClick={onUpdate}>
          Update my password
        </AppButton>
      </ButtonContainer>

      <LoginLink>
        Remember your password? <Link to={routeNames.login}>Login here</Link>
      </LoginLink>
    </AuthLayout>
  );
};
